import React from 'react';
import { Composition, CompositionComponent, CompositionProcess } from '../types/costwand.d';

interface CompositionViewerProps {
  composition: Composition;
  total_cost: string; // Changed type to string as it's already formatted
}

// Helper function to calculate the total cost for a single process instance
const calculateProcessInstanceCost = (process: CompositionProcess): number => {
  // Ensure values are treated as numbers and handle potential undefined values
  const cost = Number(process.cost || 0);
  const requiredQty = Number(process.required_qty || 0);
  return parseFloat((cost * requiredQty * Number(process.relative_cost || 1)).toFixed(5));
};

// Recursive helper function to calculate the effective unit cost of a component.
// This includes its own direct cost and the costs of its direct children (processes and subcomponents),
// all normalized to ONE unit of the current component.
// It does NOT include the current component's required_qty or overhead_charge.
// This function calculates the *raw* effective unit cost, without applying any overhead.
const calculateComponentRawEffectiveUnitCost = (component: CompositionComponent): number => {
  // Ensure values are treated as numbers
  const componentCostPerUnit = Number(component.cost || 0) / Math.max(Number(component.purchase_quantity || 1), 0.000001); // Divide by purchase_quantity
  let unitCost = parseFloat((componentCostPerUnit * Number(component.required_qty || 0)).toFixed(5)); // Start with the component's own direct unit cost

  // Add the cost of its direct processes (per unit of this component)
  if (component.processes) {
    unitCost += component.processes.reduce((sum, p) => sum + calculateProcessInstanceCost(p), 0);
  }

  // Add the cost of its direct subcomponents (per unit of this component)
  if (component.subcomponents) {
    unitCost += component.subcomponents.reduce((sum, sc) => {
      // Recursively get the raw effective unit cost of the subcomponent,
      // then multiply by its required_qty for *this* parent component.
      // Note: The subcomponent's own overhead is not applied here, as this function calculates "raw" unit cost.
      return sum + parseFloat((calculateComponentRawEffectiveUnitCost(sc) * Number(sc.required_qty || 0)).toFixed(5));
    }, 0);
  }

  return unitCost;
};

// Recursive helper function to calculate the *effective* total cost for a single component instance,
// *including* its own overhead charge applied to its direct costs and processes.
// This is the value that would be "passed up" to a parent component or used for the final product total.
const calculateComponentEffectiveInstanceTotalCost = (component: CompositionComponent): number => {
  // Ensure values are treated as numbers
  const requiredQty = Number(component.required_qty || 0);
  const directCostPerUnit = Number(component.cost || 0) / Math.max(Number(component.purchase_quantity || 1), 0.000001); // Divide by purchase_quantity
  const overheadCharge = Number(component.overhead_charge || 0);
  
  // 2. Apply this component's overhead charge ONLY to its direct material and direct processes cost.
  const overheadMultiplier = 1 + (overheadCharge / 100);
  
  // 1. Calculate the base cost for this component's own direct material and direct processes, per unit.
  let componentDirectAndProcessUnitCost = directCostPerUnit * requiredQty * overheadMultiplier;
  if (component.processes) {
    componentDirectAndProcessUnitCost += component.processes.reduce((sum, p) => sum + calculateProcessInstanceCost(p), 0);
  }

  const componentDirectAndProcessCostWithOverhead = componentDirectAndProcessUnitCost;

  // 3. Multiply by this component's required quantity for its parent.
  // This is the cost of this component's own contribution (direct + processes + its own overhead)
  // for the quantity required by its parent.
  let totalCostForThisComponentLevel = componentDirectAndProcessCostWithOverhead;

  // 4. Add the total costs of its subcomponents.
  //    Each subcomponent's effective total cost is recursively calculated and already includes its own overhead.
  //    The parent's overhead is NOT applied to the subcomponent's already calculated total cost.
  if (component.subcomponents) {
    totalCostForThisComponentLevel += component.subcomponents.reduce((sum, sc) => {
      return sum + calculateComponentEffectiveInstanceTotalCost(sc); // Recursive call
    }, 0);
  }

  return totalCostForThisComponentLevel;
};


const renderProcess = (process: CompositionProcess, level: number) => {
  const processTotalCost = calculateProcessInstanceCost(process);
  return (
    <li key={process.process_id} className={`ml-${level * 4} text-sm`}>
      <span className="font-semibold">{process._labels?.process_id || `Process ID: ${process.process_id}`}:</span>
      {' '}Cost: ${Number(process.relative_cost)}, Required Qty: {Number(process.required_qty || 0).toFixed(4)}, Unit Cost: {Number(process.cost || 0).toFixed(4)}
      <span className="font-bold ml-2"> (Total: ${processTotalCost.toFixed(4)})</span>
    </li>
  );
};

const renderComponent = (component: CompositionComponent, level: number) => {
  // For display, we want the effective unit cost *before* applying the current component's overhead
  // to its subcomponents. The `calculateComponentRawEffectiveUnitCost` gives this.
  const componentRawEffectiveUnitCost = calculateComponentRawEffectiveUnitCost(component);

  // This is the actual effective total cost of the component instance, *including* its own overhead.
  // This value is used for summing up to parent components or the final product total.
  const componentEffectiveInstanceTotalCost = calculateComponentEffectiveInstanceTotalCost(component);

  return (
    <li key={component.component_id} className={`ml-${level * 4} mt-2 border-l-2 pl-2 border-gray-300`}>
      <span className="font-bold text-md">
        {component._labels?.component_id || `Component ID: ${component.component_id}`}
        {component._labels?.unit_of_measure_id && ` [${component._labels.unit_of_measure_id}]`} {/* ADDED LINE */}
        :
      </span>
      <ul className="list-disc list-inside text-sm">
        <li className={`ml-${(level + 1) * 4}`}>Required Qty: {Number(component.required_qty || 0).toFixed(4)}</li>
        <li className={`ml-${(level + 1) * 4}`}>Direct Unit Cost: ${Number(component.cost || 0).toFixed(4)}</li>
        <li className={`ml-${(level + 1) * 4}`}>Overhead Charge: {Number(component.overhead_charge || 0).toFixed(2)}%</li>
        {component.processes && component.processes.length > 0 && (
          <li className={`ml-${(level + 1) * 4}`}>
            <span className="font-semibold">Processes:</span>
            <ul className="list-disc list-inside">
              {component.processes.map((p) => renderProcess(p, level + 2))}
            </ul>
          </li>
        )}
        {component.subcomponents && component.subcomponents.length > 0 && (
          <li className={`ml-${(level + 1) * 4}`}>
            <span className="font-semibold">Subcomponents:</span>
            <ul className="list-disc list-inside">
              {component.subcomponents.map((sc) => renderComponent(sc, level + 2))}
            </ul>
          </li>
        )}
        <li className={`ml-${(level + 1) * 4} font-bold mt-1 p-1 bg-gray-100 rounded`}>
          Calculated Unit Cost (including sub-elements, before own overhead): ${componentRawEffectiveUnitCost.toFixed(4)}
        </li>
        <li className={`ml-${(level + 1) * 4} font-bold p-1 bg-blue-50 rounded text-blue-800`}>
          Total Cost for this {component._labels?.component_id || `Component ID: ${component.component_id}`} instance: ${componentEffectiveInstanceTotalCost.toFixed(4)}
        </li>
      </ul>
    </li>
  );
};

const CompositionViewer: React.FC<CompositionViewerProps> = ({ composition, total_cost }) => {
  if (!composition) {
    return <p className="text-muted-foreground">No composition data available.</p>;
  }

  return (
    <div className="space-y-4 p-2">
      {composition.processes && composition.processes.length > 0 && (
        <div>
          <h4 className="text-lg font-bold">Overall Processes:</h4>
          <ul className="list-disc list-inside">
            {composition.processes.map((p) => renderProcess(p, 0))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="text-lg font-bold">Components:</h4>
        {composition.components.length > 0 ? (
          <ul className="list-disc list-inside">
            {composition.components.map((c) => renderComponent(c, 0))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No components defined.</p>
        )}
      </div>

      <div className="mt-6 p-4 border-t-2 border-blue-500 bg-blue-100 rounded-lg shadow-md">
        <h3 className="text-xl font-extrabold text-blue-700">
          Total Product Cost: ${total_cost}
        </h3>
      </div>
    </div>
  );
};

export default CompositionViewer;